import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, Loader2 } from "lucide-react";

const ACCEPT_MAP = { video: "video/*", image: "image/*", audio: "audio/*", document: ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" };

export function UploadModal({ selectedNodeId, onClose, onSuccess }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [resourceType, setResourceType] = useState("video");
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Selecciona un archivo"); return; }
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("resource_type", resourceType);
      formData.append("topic_ids", selectedNodeId);
      formData.append("file", file);
      await api.post("/resource-library/upload", formData, {
        timeout: 120000,
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success("Recurso recibido, procesando subida a Drive...");
      onSuccess();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir el recurso");
    }
    setUploadProgress(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose} data-testid="upload-modal-overlay">
      <form onClick={e => e.stopPropagation()} onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" data-testid="upload-modal-form">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-[#1F2547]">Subir recurso</h3>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-[#F0F1F5]"><X size={16} /></button>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#3B3F5C] mb-1">Título</label>
          <input required value={title} onChange={e => setTitle(e.target.value)} className="w-full border border-[#E1E5F0] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#A43790]" data-testid="upload-title" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#3B3F5C] mb-1">Descripción (obligatoria)</label>
          <textarea required value={description} onChange={e => setDescription(e.target.value)} rows={3} className="w-full border border-[#E1E5F0] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#A43790] resize-none" data-testid="upload-description" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#3B3F5C] mb-1">Tipo de recurso</label>
          <select value={resourceType} onChange={e => { setResourceType(e.target.value); setFile(null); }} className="w-full border border-[#E1E5F0] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#A43790]" data-testid="upload-type">
            <option value="video">Video</option>
            <option value="image">Imagen</option>
            <option value="audio">Audio</option>
            <option value="document">Documento (PDF/Word)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#3B3F5C] mb-1">Archivo</label>
          <input type="file" accept={ACCEPT_MAP[resourceType]} onChange={e => setFile(e.target.files[0] || null)} className="w-full text-sm" data-testid="upload-file" />
        </div>
        <button type="submit" disabled={uploadProgress !== null} className="w-full py-2.5 rounded-xl bg-[#A43790] text-white text-sm font-bold hover:bg-[#7B2A6C] transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="upload-submit">
          {uploadProgress !== null ? <><Loader2 size={14} className="animate-spin" /> Subiendo... {uploadProgress}%</> : <><Upload size={14} /> Subir</>}
        </button>
      </form>
    </div>
  );
}
